/**
 * InfinitSales - File Upload Service
 * Service for uploading files to Azure Blob Storage
 */

import api from '../global/api';

const FILES_BASE = '/api/files';

// Allowed file types
const ALLOWED_TYPES = {
  // Images
  'image/jpeg': { type: 'image', maxSize: 25 },
  'image/png': { type: 'image', maxSize: 25 },
  'image/gif': { type: 'image', maxSize: 25 },
  'image/webp': { type: 'image', maxSize: 25 },
  
  // Videos
  'video/mp4': { type: 'video', maxSize: 25 },
  'video/webm': { type: 'video', maxSize: 25 },
  'video/quicktime': { type: 'video', maxSize: 25 },
  
  // Audio
  'audio/mpeg': { type: 'audio', maxSize: 25 },
  'audio/mp3': { type: 'audio', maxSize: 25 },
  'audio/wav': { type: 'audio', maxSize: 25 },
  'audio/ogg': { type: 'audio', maxSize: 25 },
  'audio/mp4': { type: 'audio', maxSize: 25 },
  // Voice notes gravadas no navegador (MediaRecorder)
  'audio/webm': { type: 'audio', maxSize: 25 },
  
  // Documents
  'application/pdf': { type: 'pdf', maxSize: 25 },
  'application/msword': { type: 'document', maxSize: 25 },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { type: 'document', maxSize: 25 },
  'application/vnd.ms-excel': { type: 'document', maxSize: 25 },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { type: 'document', maxSize: 25 },
  'application/vnd.ms-powerpoint': { type: 'document', maxSize: 25 },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { type: 'document', maxSize: 25 },
  'text/plain': { type: 'document', maxSize: 25 },
  'text/csv': { type: 'document', maxSize: 25 },
  'application/zip': { type: 'document', maxSize: 25 },
};

// Teto de INGESTAO (o mesmo do backend): o maior arquivo que aceitamos enviar
// para o servidor tentar comprimir. NAO e o limite do WhatsApp — quem manda
// nesse e o canal, e o backend comprime foto/video que passar dele, como o
// proprio WhatsApp faz. Antes havia um corte fixo de 25MB aqui, e um video de
// celular era recusado no navegador sem nem tentar.
const MAX_FILE_SIZE_MB = 100;

// Upload de midia grande nao cabe no timeout global de 30s do axios: 60MB em
// upload domestico leva minutos, e ainda ha a compressao no servidor.
const UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * File Upload Service
 */
class FileUploadService {
  /**
   * Validate a file before upload
   * @param {File} file - File object from input
   * @returns {Object} { valid: boolean, error?: string, type?: string }
   */
  validateFile(file, options = {}) {
    const { asDocument = false } = options;

    if (!file) {
      return { valid: false, error: 'Nenhum arquivo selecionado' };
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      return {
        valid: false,
        error: `Arquivo muito grande. Máximo: ${MAX_FILE_SIZE_MB}MB`
      };
    }

    if (file.size === 0) {
      return { valid: false, error: 'Arquivo vazio' };
    }

    // "Documento" aceita qualquer extensao — e o caminho que o WhatsApp usa
    // para mandar arquivo cru (um .mov de qualidade, por exemplo).
    if (asDocument) {
      return { valid: true, type: 'document' };
    }

    // Check file type
    const typeInfo = ALLOWED_TYPES[file.type];
    if (!typeInfo) {
      // Try to detect by extension
      const ext = file.name.split('.').pop()?.toLowerCase();
      const extTypeMap = {
        jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image',
        mp4: 'video', webm: 'video', mov: 'video', avi: 'video',
        mp3: 'audio', wav: 'audio', ogg: 'audio', m4a: 'audio',
        pdf: 'pdf', doc: 'document', docx: 'document', 
        xls: 'document', xlsx: 'document', ppt: 'document', pptx: 'document',
        txt: 'document', csv: 'document', zip: 'document',
      };

      if (!extTypeMap[ext]) {
        return { 
          valid: false, 
          error: 'Tipo de arquivo não suportado. Use: imagens, vídeos, áudios ou documentos.' 
        };
      }

      return { valid: true, type: extTypeMap[ext] };
    }

    return { valid: true, type: typeInfo.type };
  }

  /**
   * Upload a single file
   * @param {File} file - File object from input
   * @param {Function} onProgress - Progress callback (0-100)
   * @returns {Promise<Object>} Uploaded file info
   */
  async uploadFile(file, onProgress = null, options = {}) {
    const { channel = null, asDocument = false } = options;

    // Validate first
    const validation = this.validateFile(file, { asDocument });
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);
    if (channel) formData.append('channel', channel);
    if (asDocument) formData.append('as_document', 'true');

    console.log('[FileUpload] Uploading:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(1)}KB`,
      channel,
      asDocument,
    });

    try {
      const response = await api.post(`${FILES_BASE}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      });

      if (!response.data?.success) {
        throw new Error(response.data?.detail || 'Upload failed');
      }

      const data = response.data.data;

      console.log('[FileUpload] Success:', data.url);

      return {
        url: data.url,
        filename: data.filename,
        type: data.type,
        mimeType: data.mimeType,
        size: data.size,
        compressed: data.compressed || false,
        originalSize: data.originalSize || data.size,
        asDocument: data.asDocument || false,
      };
    } catch (error) {
      console.error('[FileUpload] Error:', error);

      // O backend recusa midia grande com um detail estruturado, para a UI
      // poder oferecer "enviar como arquivo" em vez de so mostrar o erro.
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const err = new Error(detail.message || 'Nao foi possivel enviar o arquivo');
        err.code = detail.code;
        err.canSendAsDocument = !!detail.canSendAsDocument;
        throw err;
      }

      const message = detail ||
                      error.message ||
                      'Erro ao fazer upload do arquivo';

      throw new Error(message);
    }
  }

  /**
   * Upload media de FAQ para container público (URL estável, sem expiração).
   * Usado quando a mídia será enviada via tool send_media do agente.
   */
  async uploadFaqMedia(file, onProgress = null) {
    const validation = this.validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`${FILES_BASE}/faq-media/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      });

      if (!response.data?.success) {
        throw new Error(response.data?.detail || 'Upload failed');
      }

      const data = response.data.data;
      return {
        url: data.url,
        filename: data.filename,
        type: data.type,
        mimeType: data.mimeType,
        size: data.size,
      };
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Erro ao fazer upload da mídia';
      throw new Error(message);
    }
  }

  /**
   * Upload multiple files
   * @param {FileList|File[]} files - Files to upload
   * @param {Function} onProgress - Progress callback ({ uploaded, total, currentFile })
   * @returns {Promise<Object>} { uploaded: [], errors: [] }
   */
  async uploadMultiple(files, onProgress = null, options = {}) {
    // Handle both FileList and Array
    const fileArray = files instanceof FileList ? Array.from(files) : files;
    
    if (!fileArray || fileArray.length === 0) {
      throw new Error('Nenhum arquivo selecionado');
    }

    if (fileArray.length > 10) {
      throw new Error('Máximo de 10 arquivos por vez');
    }

    console.log('[FileUpload] uploadMultiple called with:', fileArray.length, 'files');

    const results = {
      uploaded: [],
      errors: [],
    };

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      
      // Report progress
      if (onProgress) {
        onProgress({
          uploaded: i,
          total: fileArray.length,
          currentFile: file.name,
        });
      }

      try {
        const result = await this.uploadFile(file, null, options);
        results.uploaded.push(result);
      } catch (error) {
        results.errors.push({
          filename: file.name,
          error: error.message,
          canSendAsDocument: !!error.canSendAsDocument,
        });
      }
    }

    // Final progress
    if (onProgress) {
      onProgress({
        uploaded: fileArray.length,
        total: fileArray.length,
        currentFile: null,
      });
    }

    return results;
  }

  /**
   * Get accept string for file input
   * @returns {string} Accept attribute value
   */
  getAcceptString() {
    return [
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      // Videos
      'video/mp4',
      'video/webm',
      'video/quicktime',
      // Audio
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/ogg',
      'audio/mp4',
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip',
    ].join(',');
  }

  /**
   * Accept do item "Fotos e vídeos" do menu de anexo.
   * Wildcard de propósito: o iPhone entrega HEIC e o Android entrega formatos
   * de vídeo variados — listar MIMEs fixos escondia arquivos legítimos do
   * seletor. Quem valida de verdade é o backend.
   */
  getMediaAcceptString() {
    return 'image/*,video/*';
  }

  /**
   * Accept do item "Documento": qualquer arquivo, como no WhatsApp. É por aqui
   * que passa o vídeo grande que o usuário quer enviar sem perder qualidade.
   */
  getDocumentAcceptString() {
    return '*/*';
  }

  /**
   * Accept do item "Câmera" (mobile).
   */
  getCameraAcceptString() {
    return 'image/*,video/*';
  }

  /**
   * Format file size for display
   * @param {number} bytes - Size in bytes
   * @returns {string} Formatted size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
}

// Export singleton
const fileUploadService = new FileUploadService();
export default fileUploadService;